import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/common/DataTable';
import { formatDate } from '@/utils';
import { useDebounce } from '@/hooks';
import {
  useGetPaymentRequestsQuery,
  useUpdatePaymentRequestStatusMutation,
} from '@/store/api/paymentRequestApi';

import { PaymentRequest, PaymentRequestStatus } from '@/types';
import { Button } from '@/components/ui/button';
import { StatusActionModal } from './StatusActionModal';

export function DepositRequest() {
  const [search, setSearch] = useState('');
  const [remark, setRemark] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const [status, setStatus] = useState<PaymentRequestStatus | undefined>('PENDING');

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading, isError } = useGetPaymentRequestsQuery(
    {
      skip: pageIndex * pageSize,
      take: pageSize,
      search: debouncedSearch || undefined,
      status,
      requestType: 'DEPOSIT',
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [actionType, setActionType] = useState<'APPROVED' | 'REJECTED' | null>(null);

  const [updateStatus, { isLoading: isUpdating }] = useUpdatePaymentRequestStatusMutation();

  const openActionModal = (data: any, action: 'APPROVED' | 'REJECTED') => {
    setSelectedRequest(data);
    setActionType(action);
    setRemark('');
  };

  const closeModal = () => {
    setSelectedRequest(null);
    setActionType(null);
    setRemark('');
  };

  const handleConfirm = async (remark: string) => {
    if (!selectedRequest || !actionType) return;

    await updateStatus({
      id: selectedRequest.id,
      status: actionType,
      remark,
    }).unwrap();

    closeModal();
  };

  const requests: PaymentRequest[] = data?.data ?? [];
  const totalCount = data?.total ?? 0;

  const columns: ColumnDef<PaymentRequest>[] = [
    {
      header: 'Account Details',
      cell: ({ row }) => {
        const { bankAccount, upiAccount } = row.original;

        if (bankAccount) {
          return (
            <div className="text-sm">
              <div className="font-medium">{bankAccount.bankName}</div>
              <div className="text-muted-foreground">
                {bankAccount.accountHolderName} • {bankAccount.accountNumber}
              </div>
            </div>
          );
        }

        if (upiAccount) {
          return <span>{upiAccount.upiId}</span>;
        }

        return <span className="text-muted-foreground">—</span>;
      },
    },
    {
      header: 'Amount',
      cell: ({ row }) => <span>₹{row.original.amount}</span>,
    },
    {
      header: 'Method',
      cell: ({ row }) => <span>{row.original.methodType}</span>,
    },

    {
      header: 'Screenshot',
      cell: ({ row }) =>
        row.original.screenshot ? (
          <a href={row.original.screenshot} target="_blank" rel="noopener noreferrer">
            <img
              src={row.original.screenshot}
              alt="Screenshot"
              className="h-10 w-10 rounded cursor-pointer hover:opacity-80"
            />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;

        const statusStyles: Record<string, string> = {
          PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          APPROVED: 'bg-green-100 text-green-800 border-green-200',
          REJECTED: 'bg-red-100 text-red-800 border-red-200',
        };

        return (
          <span
            className={`inline-flex items-center capitalize rounded-full border px-3 py-1 text-xs font-medium ${
              statusStyles[status]
            }`}
          >
            {status.toLowerCase()}
          </span>
        );
      },
    },
    {
      header: 'Created',
      cell: ({ row }) => <span>{formatDate(new Date(row.original.createdAt))}</span>,
    },
    {
      header: 'Action',
      cell: ({ row }) => (
        <div className="flex gap-2 justify-start">
          <Button
            disabled={row.original.status !== 'PENDING'}
            size="sm"
            onClick={() => openActionModal(row.original, 'APPROVED')}
          >
            Approve
          </Button>

          <Button
            size="sm"
            disabled={row.original.status !== 'PENDING'}
            variant="destructive"
            onClick={() => openActionModal(row.original, 'REJECTED')}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageIndex(0);
  };

  const handleStatusChange = (value: PaymentRequestStatus | 'ALL') => {
    setStatus(value === 'ALL' ? undefined : value);
    setPageIndex(0);
  };

  if (isLoading) return <div className="p-6">Loading deposit requests…</div>;
  if (isError) return <div className="p-6 text-red-500">Failed to load data</div>;

  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-bold tracking-tight">Deposit Requests</h2>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={status ?? 'ALL'} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        data={requests}
        columns={columns}
        pageSize={pageSize}
        pageIndex={pageIndex}
        pageCount={Math.ceil(totalCount / pageSize)}
        manualPagination
        enableFilters={false}
        onSearchChange={handleSearchChange}
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;

          setPageIndex(next.pageIndex);
          setPageSize(next.pageSize);
        }}
      />

      <StatusActionModal
        open={!!selectedRequest}
        onClose={closeModal}
        data={selectedRequest}
        action={actionType!}
        onConfirm={handleConfirm}
        loading={isUpdating}
        remark={remark}
        setRemark={setRemark}
        requestType="DEPOSIT"
      />
    </div>
  );
}
